import Qonversion, { QonversionConfigBuilder, Environment, UserPropertyKey, LogLevel } from '@qonversion/web-sdk';

// Operation types that can be queued
type QonversionOperation = {
  type: 'identify' | 'checkEntitlements' | 'logout';
  params?: any[];
  resolve: (value: any) => void;
  reject: (reason: any) => void;
};

/**
 * Centralized service for managing Qonversion operations on web platforms.
 * Ensures Qonversion is properly initialized before any operations are performed.
 * Implements operation queueing for operations requested before initialization completes.
 */
class QonversionService {
  private static instance: QonversionService;
  private initializationPromise: Promise<void> | null = null;
  private isInitialized = false;
  private operationQueue: QonversionOperation[] = [];
  private initializationTimeout = 10000; // 10 seconds timeout

  private constructor() {
    // Private constructor to enforce singleton
  }

  /**
   * Get the singleton instance of QonversionService
   */
  public static getInstance(): QonversionService {
    if (!QonversionService.instance) {
      QonversionService.instance = new QonversionService();
    }
    return QonversionService.instance;
  }

  /**
   * Initialize Qonversion with the provided project key
   * Returns a promise that resolves when initialization is complete
   */
  public initialize(): Promise<void> {
    // If already initialized or initializing, return the existing promise
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    console.log('[QonversionService] Starting initialization');

    this.initializationPromise = new Promise<void>((resolve, reject) => {
      try {
        // Get Qonversion project key from environment variables
        const QONVERSION_PROJECT_KEY = process.env.NEXT_PUBLIC_QONVERSION_PROJECT_KEY || '';

        if (!QONVERSION_PROJECT_KEY) {
          const error = new Error('Missing QONVERSION_PROJECT_KEY environment variable');
          console.error('[QonversionService]', error);
          reject(error);
          return;
        }

        // Add timeout to handle initialization that never completes
        const timeoutId = setTimeout(() => {
          if (!this.isInitialized) {
            const timeoutError = new Error('Qonversion initialization timed out');
            console.error('[QonversionService]', timeoutError);
            reject(timeoutError);
          }
        }, this.initializationTimeout);

        // Create configuration
        const config = new QonversionConfigBuilder(QONVERSION_PROJECT_KEY)
          // .setLogLevel(LogLevel.Verbose)
          // .setLogTag('ARI')
          .setEnvironment(this._getQonversionEnvironment())
          .build();

        // Initialize Qonversion with the config
        Qonversion.initialize(config);

        // Mark as initialized and process any queued operations
        this.isInitialized = true;
        console.log('[QonversionService] Initialized successfully');
        clearTimeout(timeoutId);

        // Process any operations that were queued during initialization
        this.processQueue();

        resolve();
      } catch (error) {
        console.error('[QonversionService] Initialization failed:', error);
        reject(error);
      }
    });

    return this.initializationPromise;
  }

  /**
   * Determine the Qonversion environment based on environment variables.
   * Defaults to Sandbox unless NEXT_PUBLIC_QONVERSION_PRODUCTION_MODE is explicitly 'True'.
   */
  private _getQonversionEnvironment(): Environment {
    const isProduction = process.env.NEXT_PUBLIC_QONVERSION_PRODUCTION_MODE === 'True';
    console.log(`[QonversionService] Web Production Mode: ${isProduction}`);
    return isProduction ? Environment.Production : Environment.Sandbox;
  }

  /**
   * Process any operations that were queued while waiting for initialization
   */
  private processQueue(): void {
    console.log(`[QonversionService] Processing queue with ${this.operationQueue.length} operations`);

    while (this.operationQueue.length > 0) {
      const operation = this.operationQueue.shift();
      if (!operation) continue;

      try {
        switch (operation.type) {
          case 'identify':
            this.executeIdentify(
              operation.params?.[0] as string,
              operation.params?.[1] as string | undefined,
              operation.resolve,
              operation.reject
            );
            break;
          case 'checkEntitlements':
            this.executeCheckEntitlements(operation.resolve, operation.reject);
            break;
          case 'logout':
            this.executeLogout(operation.resolve, operation.reject);
            break;
        }
      } catch (error) {
        console.error(`[QonversionService] Error executing queued operation ${operation.type}:`, error);
        operation.reject(error);
      }
    }
  }

  /**
   * Identify a user in Qonversion
   * If Qonversion is not initialized, the operation will be queued
   */
  public identifyUser(userId: string, email?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isInitialized) {
        console.log('[QonversionService] Queueing identify operation until initialization completes');
        this.operationQueue.push({
          type: 'identify',
          params: [userId, email],
          resolve,
          reject
        });

        // Ensure Qonversion is initialized
        this.initialize().catch(reject);
        return;
      }

      this.executeIdentify(userId, email, resolve, reject);
    });
  }

  private async executeIdentify(
    userId: string,
    email: string | undefined,
    resolve: (value: void) => void,
    reject: (reason: any) => void
  ): Promise<void> {
    try {
      if (!userId) {
        console.warn('[QonversionService] Cannot identify Qonversion user without a userId');
        resolve();
        return;
      }

      console.log('[QonversionService] Identifying user:', { userId, email });
      const qonversionInstance = await Qonversion.getSharedInstance();
      await qonversionInstance.identify(userId);

      if (email) {
        await qonversionInstance.setUserProperty(UserPropertyKey.Email, email);
      }

      console.log('[QonversionService] User identified successfully');
      resolve();
    } catch (error) {
      console.error('[QonversionService] Error identifying user:', error);
      reject(error);
    }
  }

  /**
   * Check user entitlements
   * If Qonversion is not initialized, the operation will be queued
   */
  public checkEntitlements(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.isInitialized) {
        console.log('[QonversionService] Queueing checkEntitlements operation until initialization completes');
        this.operationQueue.push({
          type: 'checkEntitlements',
          resolve,
          reject
        });

        // Ensure Qonversion is initialized
        this.initialize().catch(reject);
        return;
      }

      this.executeCheckEntitlements(resolve, reject);
    });
  }

  private async executeCheckEntitlements(
    resolve: (value: any) => void,
    reject: (reason: any) => void
  ): Promise<void> {
    try {
      console.log('[QonversionService] Checking entitlements');
      const qonversionInstance = await Qonversion.getSharedInstance();
      const entitlements = await qonversionInstance.entitlements();

      console.log('[QonversionService] Entitlements response:', entitlements);
      resolve(entitlements);
    } catch (error) {
      console.error('[QonversionService] Error checking entitlements:', error);
      reject(error);
    }
  }

  /**
   * Logout the current user from Qonversion
   * If Qonversion is not initialized, the operation will be queued
   */
  public logout(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isInitialized) {
        console.log('[QonversionService] Queueing logout operation until initialization completes');
        this.operationQueue.push({
          type: 'logout',
          resolve,
          reject
        });

        // Ensure Qonversion is initialized
        this.initialize().catch(reject);
        return;
      }

      this.executeLogout(resolve, reject);
    });
  }

  private async executeLogout(
    resolve: (value: void) => void,
    reject: (reason: any) => void
  ): Promise<void> {
    try {
      console.log('[QonversionService] Logging out user');
      const qonversionInstance = await Qonversion.getSharedInstance();
      await qonversionInstance.logout();
      console.log('[QonversionService] User logged out successfully');
      resolve();
    } catch (error) {
      console.error('[QonversionService] Error logging out user:', error);
      reject(error);
    }
  }
}

export default QonversionService;